import type { ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { ONBOARDING_PATHS } from './constants/paths';
import { OnboardingLayout } from './components/OnboardingLayout';
import { ONBOARDING_STEPS, type OnboardingStep } from './constants/onboarding-steps';
import { useWebBuilderAccess } from '../../hooks/useWebBuilderAccess';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';

function StepCard({ step }: { step: OnboardingStep }): ReactElement {
  return (
    <div
      className={cn(
        'vtwo:flex vtwo:flex-col vtwo:gap-4 vtwo:flex-1 vtwo:min-w-[160px] vtwo:md:max-w-[50%]',
        {
          'vtwo:basis-full vtwo:md:basis-[0%]': step.id === 'preview',
        }
      )}
    >
      <img src={step.icon} alt="" className="vtwo:size-10" />

      <div className="vtwo:flex vtwo:flex-col vtwo:gap-1">
        <span className="vtwo:text-xs vtwo:font-medium vtwo:text-gray-500 vtwo:uppercase vtwo:tracking-wide">
          {step.label}
        </span>
        <p className="vtwo:text-sm vtwo:font-bold">{step.title}</p>
        <p className="vtwo:text-sm vtwo:text-gray-600">{step.description}</p>
      </div>

      {step.need && (
        <>
          <Separator />
          <div className="vtwo:flex vtwo:flex-col vtwo:gap-1">
            <p className="vtwo:text-sm vtwo:font-semibold">What you'll need:</p>
            <p className="vtwo:text-sm vtwo:text-gray-600">{step.need}</p>
          </div>
        </>
      )}
    </div>
  );
}

const StepsOverviewPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasWebBuilderAccess } = useWebBuilderAccess();
  const { trackOnboardingStarted } = useOnboardingTracker();
  // Organisations that already own the builder never see the preview upsell step.
  const steps = hasWebBuilderAccess
    ? ONBOARDING_STEPS.filter((step) => step.id !== 'preview')
    : ONBOARDING_STEPS;

  return (
    <OnboardingLayout
      backTo={ONBOARDING_PATHS.base}
      primaryLabel="Continue"
      onPrimary={() => {
        trackOnboardingStarted();
        navigate({ pathname: ONBOARDING_PATHS.colour, search: searchParams.toString() });
      }}
    >
      <h1 className="vtwo:text-4xl vtwo:font-bold vtwo:text-center vtwo:mb-10">
        Here are your steps:
      </h1>

      <div className="vtwo:flex vtwo:flex-row vtwo:flex-wrap vtwo:gap-8">
        {steps.map((step) => (
          <StepCard key={step.label} step={step} />
        ))}
      </div>
    </OnboardingLayout>
  );
};

export default StepsOverviewPage;
